import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { DropdownData, ProjectDropdownData } from '../models/dropdown.models';
import { DealLookup, Lookup } from '../models/lookup.models';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { DealStatus, DealStatusLabels } from '../enums/deal-status.enum';
import { Project } from '@zayan-erp/home-building';

@Injectable({
  providedIn: 'root'
})
export class LookupsService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  readVarianceReasons(): Observable<Lookup[]> {
    return this.#http.getData<Lookup[]>(`${this.baseUrl}/lookup/variance-reasons`, {
      showLoader: true,
      showMessage: true
    });
  }

  readUofm(): Observable<Lookup[]> {
    return this.#http.getData<Lookup[]>(`${this.baseUrl}/lookup/uofms`, { showLoader: true, showMessage: false });
  }

  readDealLookups(filters: { statusIds?: string[]; salesOfficeId?: string | null } = {}): Observable<DealLookup[]> {
    const params = new URLSearchParams({ PageSize: '1000' });
    filters.statusIds?.forEach(id => params.append('Status', id));
    if (filters.salesOfficeId) params.set('SalesOfficeId', filters.salesOfficeId);

    return this.#http
      .getData<DealLookup[]>(`${this.baseUrl}/lookup/deals?${params.toString()}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map((deals: DealLookup[] | null) => deals ?? []));
  }

  readLookupProjects(showLoader = true): Observable<DropdownData[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/projects`, { showLoader, showMessage: false }).pipe(
      map(
        rawData =>
          (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.projCode,
            secondLabel: item.projectName,
            data: {
              currencyCode: item.currencyCode
            }
          })) as DropdownData[]
      )
    );
  }
  readProjects(): Observable<Project[]> {
    return this.#http.getData<Project[]>(`${this.baseUrl}/lookup/projects`, { showLoader: true, showMessage: false });
  }

  readLookupLots(): Observable<DropdownData[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/lots`, { showLoader: true, showMessage: false }).pipe(
      map(
        rawData =>
          (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.lotNo,
            secondLabel: item.projectCode
          })) as DropdownData[]
      )
    );
  }

  readLookupProjectLots(
    projectId: string,
    showLoader = true,
    includingInActive = true,
    showError = true
  ): Observable<{ [projectid: string]: DropdownData[] }> {
    const query = includingInActive ? '' : '?includingInActive=false';
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/${projectId}/lots${query}`, {
        showLoader,
        showMessage: false,
        showError
      })
      .pipe(
        map(rawData => {
          const lots = (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.lotNo,
            secondLabel: item.projectCode
          })) as DropdownData[];
          return { [projectId]: lots };
        })
      );
  }

  readLookupWarrantyTypes(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/warranty-types`, { showLoader: true, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.typeCode,
              secondLabel: item.typeName
            })) as DropdownData[]
        )
      );
  }

  readLookupWarrantyDeficiencies(includingInActive: boolean = false): Observable<DropdownData[]> {
    return this.#http
      .getData<
        any[]
      >(`${this.baseUrl}/lookup/warranty-deficiencies?includingInActive=${includingInActive}`, { showLoader: true, showMessage: true })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.deficiencyCode,
              secondLabel: item.deficiencyDescription
            })) as DropdownData[]
        )
      );
  }
  readLocations(includingInActive: boolean = false, showLoader = true): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/locations?includingInActive=${includingInActive}`, {
        showLoader,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.locationCode,
              secondLabel: item.location
            })) as DropdownData[]
        )
      );
  }
  readCategoryLocations(categoryId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/locations?categoryId=${categoryId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.locationCode,
              secondLabel: item.location
            })) as DropdownData[]
        )
      );
  }

  readSalesOffices(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/sales-offices`, { showLoader: true, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name
            })) as DropdownData[]
        )
      );
  }

  readSalesOfficesWithProjectCount(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/SalesOffice/with-project-count`, { showLoader: false, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name,
              secondLabel: item.projectCount?.toString() ?? '0'
            })) as DropdownData[]
        )
      );
  }
  readSuppliers(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/supplier-addresses`, {
        showLoader: true,
        showMessage: true
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name,
              secondLabel: item.email
            })) as DropdownData[]
        )
      );
  }

  readModelsByConstructionType(constructionTypeId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/models/by-construction-type/${constructionTypeId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.modelName
            })) as DropdownData[]
        )
      );
  }

  readModelsByProject(projectId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/models/by-project/${projectId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.modelName
            })) as DropdownData[]
        )
      );
  }
  readProjectsByModel(modelId: string): Observable<any[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/projects/by-model/${modelId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readSalesOfficesPublic(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/sales-offices`, { showLoader: true, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name
            })) as DropdownData[]
        )
      );
  }

  readCountries(): Observable<DropdownData[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/countries`, { showLoader: true, showMessage: false }).pipe(
      map(
        rawData =>
          (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.name
          })) as DropdownData[]
      )
    );
  }

  readProjectTypes(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/project-types`, { showLoader: true, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.code,
              secondLabel: item.name
            })) as DropdownData[]
        )
      );
  }
  readUsersByRole(roleId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/users-by-role?roleId=${roleId}`, {
        showLoader: true,
        showMessage: true
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.firstName,
              secondLabel: item.lastName
            })) as DropdownData[]
        )
      );
  }

  readUsersList(moduleId?: string): Observable<DropdownData[]> {
    const query = moduleId ? `?moduleId=${moduleId}` : '';
    return this.#http
      .getData<any[]>(`${this.#apiConfig.baseUrlV2}/lookup/users${query}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(
        map(rawData => {
          return (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.firstName,
            secondLabel: item.lastName,
            data: { email: item.email }
          })) as DropdownData[];
        })
      );
  }

  readDealStatuses(): Observable<DropdownData[]> {
    const dealStatuses: DropdownData[] = Object.values(DealStatus).map(statusId => ({
      id: statusId,
      firstLabel: DealStatusLabels[statusId as DealStatus]
    }));
    return of(dealStatuses);
  }

  readTasks(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/tasks?includingInActive=true`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.taskCode,
              secondLabel: item.taskName
            })) as DropdownData[]
        )
      );
  }

  readTemplateVariables(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/template-variables`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name
            })) as DropdownData[]
        )
      );
  }

  readOperatingUnits(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/operating-units`, { showLoader: true, showMessage: false })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.name || item.unitName,
              secondLabel: item.oUnitCode || item.name || item.unitCode
            })) as DropdownData[]
        )
      );
  }

  readProjectsByOUnitCodeId(OUnitCodeId: string): Observable<ProjectDropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/${OUnitCodeId}/projects`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.projCode || item.code,
              secondLabel: item.projectName || item.name,
              productListId: item.productListId,
              currencyCode: item.currencyCode
            })) as ProjectDropdownData[]
        )
      );
  }
  readUOMs(): Observable<DropdownData[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/uofms`, { showLoader: true, showMessage: false }).pipe(
      map(
        rawData =>
          (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.code,
            secondLabel: item.description
          })) as DropdownData[]
      )
    );
  }

  readNoteTypes(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/notetypes?includeInactive=false`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.noteTypeCode,
              secondLabel: item.description
            })) as DropdownData[]
        )
      );
  }

  readLotTypes(): Observable<DropdownData[]> {
    return this.#http.getData<any[]>(`${this.baseUrl}/lookup/lot-types`, { showLoader: true, showMessage: false }).pipe(
      map(
        rawData =>
          (rawData ?? []).map((item: any) => ({
            id: item.id,
            firstLabel: item.name
          })) as DropdownData[]
      )
    );
  }

  readDealCancellationReasons(): Observable<DropdownData[]> {
    return this.#http
      .getData<any[]>(`${this.baseUrl}/lookup/deal-cancellation-reasons`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map(
          rawData =>
            (rawData ?? []).map((item: any) => ({
              id: item.id,
              firstLabel: item.code || item.name || item.reasonCode,
              secondLabel: item.description || item.name
            })) as DropdownData[]
        )
      );
  }
}
